import React, { useState, useEffect, useMemo } from "react"
import {
  Box,
  Button,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Badge,
  IconButton,
  Spinner,
  Tooltip,
  Input,
  Checkbox,
  Collapse,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { ChevronDownIcon, ChevronUpIcon, CheckIcon, CloseIcon, DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

const OrdersDashboard = ({ latestUploadId }) => {
  const { selectedSupplier } = useSupplierRetailer()
  const [uploads, setUploads] = useState([])
  const [selectedUpload, setSelectedUpload] = useState("")
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [expandedStores, setExpandedStores] = useState({})
  const [statusFilter, setStatusFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [editingComment, setEditingComment] = useState({})
  const toast = useToast()

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const expandBg = useColorModeValue("blue.50", "blue.900")
  const approvedBg = useColorModeValue("green.50", "green.900")
  const rejectedBg = useColorModeValue("red.50", "red.900")

  useEffect(() => {
    if (selectedSupplier) fetchUploads()
  }, [selectedSupplier])

  useEffect(() => {
    if (latestUploadId) {
      setSelectedUpload(latestUploadId)
    }
  }, [latestUploadId])

  useEffect(() => {
    if (selectedUpload) fetchOrders()
  }, [selectedUpload])

  const fetchUploads = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/orders/get-uploads`, {
        supplier: selectedSupplier,
      })
      if (res.data.status === 1) {
        setUploads(res.data.data)
        if (res.data.data.length > 0 && !selectedUpload) {
          setSelectedUpload(res.data.data[0].upload_id)
        }
      }
    } catch (err) {
      console.error("fetchUploads error:", err)
    }
  }

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/orders/get-orders`, {
        upload_id: selectedUpload,
      })
      if (res.data.status === 1) {
        setOrders(res.data.data)
        const stores = {}
        res.data.data.forEach((o) => { stores[o.store_code] = true })
        setExpandedStores(stores)
      }
    } catch (err) {
      console.error("fetchOrders error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (id, status, lineComment) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/orders/update-status`, {
        id,
        status,
        line_comment: lineComment || null,
        userid: localStorage.getItem("userid"),
      })
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status, line_comment: lineComment || o.line_comment } : o))
      )
    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error", duration: 3000 })
    }
  }

  const handleBulkUpdate = async (status) => {
    if (selectedIds.size === 0) return
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/orders/bulk-update-status`, {
        ids: Array.from(selectedIds),
        status,
        userid: localStorage.getItem("userid"),
      })
      setOrders((prev) =>
        prev.map((o) => (selectedIds.has(o.id) ? { ...o, status } : o))
      )
      setSelectedIds(new Set())
      toast({ title: `${selectedIds.size} orders ${status}`, status: "success", duration: 2000 })
    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error", duration: 3000 })
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/orders/download-report`,
        { upload_id: selectedUpload },
        { responseType: "blob" }
      )
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "orders_report.xlsx")
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast({ title: "Download failed", description: err.message, status: "error", duration: 3000 })
    } finally {
      setDownloading(false)
    }
  }

  const handleLineCommentSave = async (id, comment) => {
    await handleStatusUpdate(id, orders.find((o) => o.id === id)?.status || "pending", comment)
    setEditingComment({})
  }

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        return (
          (o.store_code || "").toLowerCase().includes(term) ||
          (o.store_description || "").toLowerCase().includes(term) ||
          (o.description || "").toLowerCase().includes(term) ||
          (o.vendor_product_code || "").toLowerCase().includes(term) ||
          (o.po_number || "").toLowerCase().includes(term)
        )
      }
      return true
    })
  }, [orders, statusFilter, searchTerm])

  const groupedOrders = useMemo(() => {
    const groups = {}
    filteredOrders.forEach((o) => {
      const key = o.store_code || "UNKNOWN"
      if (!groups[key]) groups[key] = { store_code: key, store_description: o.store_description, store_comment: o.store_comment, orders: [] }
      groups[key].orders.push(o)
    })
    return Object.values(groups).sort((a, b) => a.store_code.localeCompare(b.store_code))
  }, [filteredOrders])

  const toggleStore = (code) => {
    setExpandedStores((prev) => ({ ...prev, [code]: !prev[code] }))
  }

  const toggleSelectAll = (storeOrders) => {
    const allSelected = storeOrders.every((o) => selectedIds.has(o.id))
    const newSet = new Set(selectedIds)
    storeOrders.forEach((o) => {
      if (allSelected) newSet.delete(o.id)
      else newSet.add(o.id)
    })
    setSelectedIds(newSet)
  }

  const statusBadge = (status) => {
    const colors = { approved: "green", rejected: "red", pending: "yellow" }
    return <Badge colorScheme={colors[status] || "gray"}>{status}</Badge>
  }

  const summaryStats = useMemo(() => {
    const total = orders.length
    const approved = orders.filter((o) => o.status === "approved").length
    const rejected = orders.filter((o) => o.status === "rejected").length
    const pending = orders.filter((o) => o.status === "pending").length
    return { total, approved, rejected, pending }
  }, [orders])

  if (!selectedSupplier) {
    return <Text p={4} color="gray.500">Select a supplier to view orders</Text>
  }

  return (
    <Box>
      <Flex gap={4} mb={4} align="center" wrap="wrap">
        <Select
          w="300px"
          value={selectedUpload}
          onChange={(e) => setSelectedUpload(e.target.value)}
          placeholder="Select upload..."
        >
          {uploads.map((u) => (
            <option key={u.upload_id} value={u.upload_id}>
              {new Date(u.uploaded_at).toLocaleDateString()} - {u.total_lines} lines ({u.pending_count} pending)
            </option>
          ))}
        </Select>

        <Select w="150px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>

        <Input
          w="250px"
          placeholder="Search store, article, PO..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Flex ml="auto" gap={2}>
          {selectedIds.size > 0 && (
            <>
              <Button size="sm" colorScheme="green" onClick={() => handleBulkUpdate("approved")}>
                Approve ({selectedIds.size})
              </Button>
              <Button size="sm" colorScheme="red" onClick={() => handleBulkUpdate("rejected")}>
                Reject ({selectedIds.size})
              </Button>
            </>
          )}
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleDownload}
            isDisabled={!selectedUpload || downloading}
            leftIcon={downloading ? <Spinner size="xs" /> : null}
          >
            {downloading ? "Downloading..." : "Download Report"}
          </Button>
        </Flex>
      </Flex>

      {orders.length > 0 && (
        <Flex gap={4} mb={4}>
          <Badge px={3} py={1} borderRadius="md" fontSize="sm">Total: {summaryStats.total}</Badge>
          <Badge px={3} py={1} borderRadius="md" fontSize="sm" colorScheme="yellow">Pending: {summaryStats.pending}</Badge>
          <Badge px={3} py={1} borderRadius="md" fontSize="sm" colorScheme="green">Approved: {summaryStats.approved}</Badge>
          <Badge px={3} py={1} borderRadius="md" fontSize="sm" colorScheme="red">Rejected: {summaryStats.rejected}</Badge>
        </Flex>
      )}

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
      ) : groupedOrders.length === 0 ? (
        <Text p={4} color="gray.500">{selectedUpload ? "No orders found" : "Select an upload to view orders"}</Text>
      ) : (
        <Box overflowX="auto">
          {groupedOrders.map((group) => (
            <Box key={group.store_code} mb={3} border="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
              <Flex
                bg={expandBg}
                px={4}
                py={2}
                cursor="pointer"
                onClick={() => toggleStore(group.store_code)}
                align="center"
                justify="space-between"
              >
                <Flex align="center" gap={3}>
                  {expandedStores[group.store_code] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  <Text fontWeight="bold">{group.store_code}</Text>
                  <Text fontSize="sm">{group.store_description}</Text>
                  <Badge>{group.orders.length} items</Badge>
                </Flex>
                {group.store_comment && (
                  <Tooltip label={group.store_comment}>
                    <Badge colorScheme="purple" maxW="300px" isTruncated>
                      {group.store_comment}
                    </Badge>
                  </Tooltip>
                )}
              </Flex>

              <Collapse in={expandedStores[group.store_code]}>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr bg={headerBg}>
                        <Th w="30px">
                          <Checkbox
                            isChecked={group.orders.every((o) => selectedIds.has(o.id))}
                            onChange={() => toggleSelectAll(group.orders)}
                            size="sm"
                          />
                        </Th>
                        <Th>PO #</Th>
                        <Th>Order Date</Th>
                        <Th>Delivery Date</Th>
                        <Th>Description</Th>
                        <Th isNumeric>Pack</Th>
                        <Th isNumeric>QTY</Th>
                        <Th isNumeric>Cost</Th>
                        <Th>PO Type</Th>
                        <Th isNumeric>SOH</Th>
                        <Th isNumeric>DROS</Th>
                        <Th isNumeric>Days Cover</Th>
                        <Th isNumeric fontWeight="bold">New Days</Th>
                        <Th>Comment</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {group.orders.map((order) => (
                        <Tr
                          key={order.id}
                          _hover={{ bg: hoverBg }}
                          bg={
                            order.status === "approved"
                              ? approvedBg
                              : order.status === "rejected"
                              ? rejectedBg
                              : "transparent"
                          }
                        >
                          <Td>
                            <Checkbox
                              isChecked={selectedIds.has(order.id)}
                              onChange={() => {
                                const newSet = new Set(selectedIds)
                                if (newSet.has(order.id)) newSet.delete(order.id)
                                else newSet.add(order.id)
                                setSelectedIds(newSet)
                              }}
                              size="sm"
                            />
                          </Td>
                          <Td fontSize="xs">{order.po_number}</Td>
                          <Td fontSize="xs">{order.order_date ? new Date(order.order_date).toLocaleDateString() : ""}</Td>
                          <Td fontSize="xs">{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : ""}</Td>
                          <Td fontSize="xs" maxW="200px" isTruncated title={order.description}>{order.description}</Td>
                          <Td isNumeric fontSize="xs">{order.pack_size}</Td>
                          <Td isNumeric fontSize="xs">{order.qty}</Td>
                          <Td isNumeric fontSize="xs">R{parseFloat(order.cost_price || 0).toFixed(2)}</Td>
                          <Td fontSize="xs">{order.po_type}</Td>
                          <Td isNumeric fontSize="xs">{order.soh_qty != null ? order.soh_qty : "-"}</Td>
                          <Td isNumeric fontSize="xs">{order.dros_qty != null ? parseFloat(order.dros_qty).toFixed(3) : "-"}</Td>
                          <Td isNumeric fontSize="xs">{order.current_days_cover != null ? parseFloat(order.current_days_cover).toFixed(1) : "-"}</Td>
                          <Td isNumeric fontSize="xs" fontWeight="bold">
                            {order.new_days_cover != null ? parseFloat(order.new_days_cover).toFixed(1) : "-"}
                          </Td>
                          <Td>
                            {editingComment[order.id] !== undefined ? (
                              <Flex gap={1}>
                                <Input
                                  size="xs"
                                  w="120px"
                                  value={editingComment[order.id]}
                                  onChange={(e) =>
                                    setEditingComment((prev) => ({ ...prev, [order.id]: e.target.value }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleLineCommentSave(order.id, editingComment[order.id])
                                    if (e.key === "Escape") setEditingComment((prev) => { const n = {...prev}; delete n[order.id]; return n })
                                  }}
                                  autoFocus
                                />
                              </Flex>
                            ) : (
                              <Text
                                fontSize="xs"
                                cursor="pointer"
                                color={order.line_comment ? "inherit" : "gray.400"}
                                onClick={() => setEditingComment({ ...editingComment, [order.id]: order.line_comment || "" })}
                                maxW="120px"
                                isTruncated
                                title={order.line_comment || "Click to add comment"}
                              >
                                {order.line_comment || "..."}
                              </Text>
                            )}
                          </Td>
                          <Td>{statusBadge(order.status)}</Td>
                          <Td>
                            <Flex gap={1}>
                              <Tooltip label="Approve">
                                <IconButton
                                  aria-label="Approve"
                                  icon={<CheckIcon />}
                                  size="xs"
                                  colorScheme="green"
                                  variant={order.status === "approved" ? "solid" : "outline"}
                                  onClick={() => handleStatusUpdate(order.id, "approved")}
                                />
                              </Tooltip>
                              <Tooltip label="Reject">
                                <IconButton
                                  aria-label="Reject"
                                  icon={<CloseIcon />}
                                  size="xs"
                                  colorScheme="red"
                                  variant={order.status === "rejected" ? "solid" : "outline"}
                                  onClick={() => handleStatusUpdate(order.id, "rejected")}
                                />
                              </Tooltip>
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default OrdersDashboard
